import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useIframeStore } from "../store";
import CloseOnboarding from "./CloseOnboarding";
import User from "./User/User";
import Name from "./Event/Name";
import GeneralAvailabilities from "./Event/GeneralAvailabilities";
import AdvancedAvailabilities from "./Event/AdvancedAvailabilities";
import SearchContacts from "./Contacts/SearchContacts";
import Notifications from "./Notifications/Notifications";
import PlanFit from "./PlanFit/PlanFit";
import Logo from "../Components/Brand/LogoFull";

function Onboarding() {
  const { t } = useTranslation(["landing", "onboarding"]);

  // Map each stage to a step index (1–5)
  const stageToStep = {
    // 1) Info
    user: 1,

    // 2) Event
    "event-name": 2,
    "event-general-availabilities": 2,
    "event-advanced-availabilities": 2,

    // 3) Search contacts (separate step)
    "search-contacts": 3,

    // 4) Notifications
    notifications: 4,

    // 5) Plan fit
    "plan-fit": 5,
  };

  const [stage, setStage] = useState("user");
  const [plan, setPlan] = useState({});

  const currentStep = stageToStep[stage] ?? 1;
  const isActive = (n) => (n <= currentStep ? "tw-text-primary-500" : "");

  const features = {
    premium: [
      t("pricing.cards.premium.1"),
      t("pricing.cards.premium.2"),
      t("pricing.cards.premium.3"),
      t("pricing.cards.premium.4"),
      t("pricing.cards.premium.5"),
    ],
    professional: [
      t("pricing.cards.professional.1"),
      t("pricing.cards.professional.2"),
      t("pricing.cards.professional.3"),
      t("pricing.cards.professional.4"),
      t("pricing.cards.professional.5"),
    ],
  };

  useEffect(() => {
    document.title = "Calendbook - Onboarding";
  }, []);

  // forced white theme
  const root = document.getElementById("root");
  const html = document.getElementById("html");
  root.setAttribute("data-theme", "light");
  html.setAttribute("data-theme", "light");

  // hide navbar and footer
  const setShouldShowNavs = useIframeStore((state) => state.setShouldShowNavs);
  setShouldShowNavs(false);
  document.getElementById("root").style.padding = 0;
  document.getElementById("root").style.minHeight = 0;

  return (
    <>
      <section className="tw-h-screen tw-bg-white tw-dark:bg-gray-900 lg:tw-py-0">
        <div className="lg:tw-flex">
          <div className="tw-flex tw-items-center tw-mx-auto md:tw-w-[50rem] tw-px-4 md:tw-px-8 xl:tw-px-0">
            <div className="tw-w-full">
              {/* Logo */}
              <div className="tw-flex tw-items-center tw-justify-center tw-my-8 tw-space-x-4 lg:tw-hidden">
                <a
                  href="#"
                  className="tw-flex tw-items-center tw-text-2xl tw-font-semibold"
                >
                  <Logo />
                </a>
              </div>

              {/* Breadcrumbs */}
              <ol className="tw-flex tw-items-center tw-mb-6 tw-text-sm tw-font-medium tw-text-center tw-text-gray-500 tw-dark:text-gray-400 lg:tw-mb-12 sm:tw-text-base">
                {/* 1 - Info */}
                <li className="tw-flex tw-items-center after:tw-content-[''] after:tw-w-12 after:tw-h-1 after:tw-border-b after:tw-border-gray-200 after:tw-border-1 after:tw-hidden sm:after:tw-inline-block after:tw-mx-6 xl:after:tw-mx-10 tw-dark:after:tw-border-gray-700">
                  <div
                    className={`${isActive(
                      1
                    )} tw-flex tw-items-center sm:tw-block after:tw-content-['/'] sm:after:tw-hidden after:tw-mx-2 after:tw-font-light after:tw-text-gray-200 tw-dark:after:tw-text-gray-500`}
                  >
                    <div className="tw-mr-2 sm:tw-mb-0 sm:tw-mx-2">1</div>
                    <span className="tw-ml-1 tw-hidden sm:tw-inline-flex">
                      {t("onboarding:header.info")}
                    </span>
                  </div>
                </li>

                {/* 2 - Event */}
                <li className="tw-flex tw-items-center after:tw-content-[''] after:tw-w-12 after:tw-h-1 after:tw-border-b after:tw-border-gray-200 after:tw-border-1 after:tw-hidden sm:after:tw-inline-block after:tw-mx-6 xl:after:tw-mx-10 tw-dark:after:tw-border-gray-700">
                  <div
                    className={`${isActive(
                      2
                    )} tw-flex tw-items-center sm:tw-block after:tw-content-['/'] sm:after:tw-hidden after:tw-mx-2 after:tw-font-light after:tw-text-gray-200 tw-dark:after:tw-text-gray-500`}
                  >
                    <div className="tw-mr-2 sm:tw-mb-0 sm:tw-mx-2">2</div>
                    <span className="tw-ml-1 tw-hidden sm:tw-inline-flex">
                      {t("onboarding:header.event")}
                    </span>
                  </div>
                </li>

                {/* 3 - Leads & Contacts */}
                <li className="tw-flex tw-items-center after:tw-content-[''] after:tw-w-12 after:tw-h-1 after:tw-border-b after:tw-border-gray-200 after:tw-border-1 after:tw-hidden sm:after:tw-inline-block after:tw-mx-6 xl:after:tw-mx-10 tw-dark:after:tw-border-gray-700">
                  <div
                    className={`${isActive(
                      3
                    )} tw-flex tw-items-center sm:tw-block after:tw-content-['/'] sm:after:tw-hidden after:tw-mx-2 after:tw-font-light after:tw-text-gray-200 tw-dark:after:tw-text-gray-500`}
                  >
                    <div className="tw-mr-2 sm:tw-mb-0 sm:tw-mx-2">3</div>
                    <span className="tw-ml-1 tw-hidden sm:tw-inline-flex">
                      {t("onboarding:header.contacts", "Leads")}
                    </span>
                  </div>
                </li>

                {/* 4 - Notifications */}
                <li className="tw-flex tw-items-center after:tw-content-[''] after:tw-w-12 after:tw-h-1 after:tw-border-b after:tw-border-gray-200 after:tw-border-1 after:tw-hidden sm:after:tw-inline-block after:tw-mx-6 xl:after:tw-mx-10 tw-dark:after:tw-border-gray-700">
                  <div
                    className={`${isActive(
                      4
                    )} tw-flex tw-items-center sm:tw-block after:tw-content-['/'] sm:after:tw-hidden after:tw-mx-2 after:tw-font-light after:tw-text-gray-200 tw-dark:after:tw-text-gray-500`}
                  >
                    <div className="tw-mr-2 sm:tw-mb-0 sm:tw-mx-2">4</div>
                    <span className="tw-ml-1 tw-hidden sm:tw-inline-flex">
                      {t("onboarding:header.notifications")}
                    </span>
                  </div>
                </li>

                {/* 5 - Plan fit (only show when in that stage) */}
                {stage === "plan-fit" && (
                  <li className="tw-flex tw-items-center sm:tw-block">
                    <div
                      className={`${isActive(
                        5
                      )} tw-flex tw-items-center sm:tw-block after:tw-content-['/'] sm:after:tw-hidden after:tw-mx-2 after:tw-font-light after:tw-text-gray-200 tw-dark:after:tw-text-gray-500`}
                    >
                      <div className="tw-ml-1 tw-mr-2 sm:tw-mb-0 sm:tw-mx-2">
                        5
                      </div>
                      <span className="tw-ml-1 tw-hidden sm:tw-inline-flex">
                        {t("onboarding:header.gift")}
                      </span>
                    </div>
                  </li>
                )}

                <CloseOnboarding stage={stage} />
              </ol>

              {stage === "user" && <User setStage={setStage} />}
              {stage === "event-name" && <Name setStage={setStage} />}
              {stage === "event-general-availabilities" && (
                <GeneralAvailabilities setStage={setStage} />
              )}
              {stage === "event-advanced-availabilities" && (
                <AdvancedAvailabilities setStage={setStage} />
              )}
              {stage === "search-contacts" && (
                <SearchContacts setStage={setStage} />
              )}
              {stage === "notifications" && (
                <Notifications setStage={setStage} />
              )}
              {stage === "plan-fit" && (
                <PlanFit plan={plan} setPlan={setPlan} />
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Onboarding;
